import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium transition-[background-color,color,box-shadow,transform,border-color] duration-150 select-none active:scale-[0.98] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-action text-white shadow-soft hover:bg-action-hover',
        navy: 'bg-navy-900 text-white shadow-soft hover:bg-navy-800 dark:bg-royal-500 dark:hover:bg-royal-400',
        secondary: 'bg-surface-muted text-ink hover:bg-mist-200 dark:hover:bg-navy-700',
        outline: 'border border-line bg-surface text-ink hover:bg-hover',
        ghost: 'text-ink hover:bg-hover',
        danger: 'bg-danger-600 text-white hover:bg-danger-500',
        'danger-ghost': 'text-danger-600 hover:bg-danger-50 dark:text-red-300 dark:hover:bg-red-500/10',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, disabled, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});
